// Global Instructions Rule Applied!
//
// Prospect Brief card — the prospect form Dashboard's process write-up,
// derived from the prospect-tracker agent's memory and the relationship
// facts per (prospect, section); cfg.prospect_brief_sections drives the
// section set and order. Read-only by design, the LpBriefCard's sibling:
// corrections flow through the facts ledger and memory ops; the brief is
// re-materialised by the next deep / zoom run.
import React, { useCallback, useEffect, useState } from 'react'
import { Tag } from 'antd'
import dayjs from 'dayjs'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { fetchProspectBrief } from '../services/internalAgentsService.ts'
import { ProspectBriefSection } from '../../../state_manager/data_models/internal_agents_model.ts'

interface ProspectBriefCardProps {
  prospectId: string
  /** Hands `load` to the prospect form so its Refresh button reloads this
   *  panel. Same contract DocumentsTab exposes via onRefreshReady. */
  onRefreshReady?: ((refreshFn: () => Promise<void>) => void) | null
  /** Render nothing until the agent has written a brief, so a prospect with
   *  no agent output keeps today's layout. */
  hideWhenEmpty?: boolean
}

export const ProspectBriefCard: React.FC<ProspectBriefCardProps> = ({
  prospectId,
  onRefreshReady = null,
  hideWhenEmpty = false
}) => {
  const [sections, setSections] = useState<ProspectBriefSection[]>([])
  const [loading, setLoading] = useState(true)
  const token = () => getAccessToken(store.getState().auth)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setSections(await fetchProspectBrief(prospectId, token()))
    } catch (error) {
      console.error('[internal-agents] prospect brief load failed:', error)
      setSections([])
    } finally {
      setLoading(false)
    }
  }, [prospectId])

  useEffect(() => {
    load()
  }, [load])

  // The app shell hides tabs with display:none rather than unmounting them, so
  // a mount-time-only fetch goes stale. `onRefreshReady` hands `load` up to the
  // prospect form so its Refresh button reloads this panel too.
  useEffect(() => {
    if (onRefreshReady) onRefreshReady(load)
  }, [onRefreshReady, load])

  const written = sections.filter((s) => s.prospect_briefs__content)
  const generatedAt = written.reduce<string | null>(
    (latest, s) =>
      s.prospect_briefs__generated_at &&
      (!latest || s.prospect_briefs__generated_at > latest)
        ? s.prospect_briefs__generated_at
        : latest,
    null
  )

  if (hideWhenEmpty && !loading && written.length === 0) {
    return null
  }

  return (
    <FormSection
      data-component='ProspectBriefCard'
      title='Prospect Brief'
      titleColor='green'
      loading={loading}
      className='shadow-sm border border-gray-200'
      headerActions={
        <span className='flex items-center gap-2'>
          <Tag color='blue'>Agent-written</Tag>
          {generatedAt ? (
            <span className='text-gray-400 text-[11.5px]'>
              updated {dayjs(generatedAt).format('MM/DD/YYYY')}
            </span>
          ) : null}
        </span>
      }
    >
      {written.length === 0 && !loading ? (
        <div className='text-gray-400 text-xs py-2'>Awaiting the prospect tracker agent's first deep run.</div>
      ) : (
        <div className='flex flex-col divide-y divide-gray-100 -mx-1'>
          {sections.map((section) => (
            <div key={section.prospect_brief_sections__section_key} className='px-1 py-2'>
              <span className='text-[11.5px] font-semibold uppercase tracking-wide text-gray-500'>
                {section.prospect_brief_sections__label}
              </span>
              {section.prospect_briefs__content ? (
                <div className='whitespace-pre-line text-[13px] mt-0.5'>
                  {section.prospect_briefs__content}
                </div>
              ) : (
                <div className='text-gray-400 text-xs mt-0.5'>Awaiting the first deep run.</div>
              )}
            </div>
          ))}
        </div>
      )}
    </FormSection>
  )
}
